import React, { Component } from "react";
import { message, Row, Col, Select, Alert } from "antd";
import { connect } from "react-redux";
import axios from "axios";
import moment from "moment";
import "moment/locale/es";
import {
  ModalItem,
  Form,
  FormItem,
  ErrorItem,
  ButtonItem,
  InputItem,
  InputNumberItem,
  SelectItem,
  DatePickerItem,
  dineroDisabledMask,
} from "./Items";

const { Option } = Select;

moment.locale("es");

/**
 * Corrige los datos de cabecera de una factura de compra ya emitida, sin tocar proveedor,
 * proyecto, insumos ni importes. Lo comparten las cuatro pantallas que listan facturas de
 * compra (Facturas, Facturas sin Orden de Compra, Facturas Caja Chica y el modal de facturas
 * de una Orden de Compra), igual que ModalEditarFechas lo comparten CxC y CxP.
 *
 * El permiso NO se revisa aqui: lo hace la pantalla que decide mostrar el boton.
 */
class ModalEditarDatosFactura extends Component {
  formRef = React.createRef();

  state = {
    loading: false,
    factura: null,
    objetoUsosDeCFDI: null,
    objetoFormasDePago: null,
    proveedorNombre: "",
    monedaAbreviacion: "",
    fechaFacturaOriginal: null,
  };

  componentDidUpdate(prevProps) {
    if (this.props.visible && !prevProps.visible) {
      this.cargarDatos();
    }
  }

  async cargarDatos() {
    if (!this.props.facturaId) return;
    this.setState({ loading: true });

    // datosFactura ya trae la factura completa y el catalogo de usos de CFDI; ninguno de los
    // selectFacturas de los listados proyecta uuid, usoDeCfdiId, descripcion ni las fechas.
    await axios
      .post(
        "facturas/datosFactura",
        { empresaId: this.props.empresaId, facturaId: this.props.facturaId },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.success !== 2) {
          message.error("No se encontro la informacion de la factura");
          return;
        }

        const factura = res.data.factura;
        const proveedor = res.data.proveedores?.[0];

        this.setState({
          factura,
          fechaFacturaOriginal: factura.fechaFactura
            ? moment(factura.fechaFactura)
            : null,
          proveedorNombre: proveedor
            ? proveedor.persona
              ? proveedor.razonSocial
              : [
                  proveedor.nombre,
                  proveedor.apellidoPaterno,
                  proveedor.apellidoMaterno,
                ]
                  .filter(Boolean)
                  .join(" ")
            : "",
          monedaAbreviacion: res.data.monedas?.[0]?.abreviacion || "",
          objetoUsosDeCFDI: (res.data.usosDeCFDI || []).map((value) => (
            <Option value={value._id} key={value._id}>
              {value.c_UsoCFDI + " - " + value.descripcion}
            </Option>
          )),
        });

        // El Form de Items monta tarde, por eso el timeout (mismo truco que ModalEditarFechas)
        setTimeout(() => {
          if (!this.formRef.current) return;
          this.formRef.current.setFieldsValue({
            codigoFactura: factura.codigoFactura,
            uuid: factura.uuid,
            usoDeCfdiId: factura.usoDeCfdiId,
            formaDePagoId: factura.formaDePagoId,
            metodoPago: factura.metodoPago,
            descripcion: factura.descripcion,
            diasCredito: factura.diasCredito,
            // Los pickers son antd v4: van con moment, nunca con dayjs.
            fechaFactura: factura.fechaFactura ? moment(factura.fechaFactura) : null,
            fechaPago: factura.fechaPago ? moment(factura.fechaPago) : null,
            fechaVencimiento: factura.fechaVencimiento
              ? moment(factura.fechaVencimiento)
              : null,
          });
        }, 100);
      })
      .catch((err) => {
        console.error(err);
        message.error("Error de conexion");
      });

    await axios
      .post(
        "formasdepago/selectFormasDePago",
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.status === 404) return;
        this.setState({
          objetoFormasDePago: res.data.map((value) => (
            <Option value={value._id} key={value._id}>
              {value?.c_FormaPago + " - " + value.descripcion}
            </Option>
          )),
        });
      })
      .catch((err) => console.error(err));

    this.setState({ loading: false });
  }

  cerrarModal(exitoso = false) {
    if (this.formRef.current) {
      this.formRef.current.resetFields();
    }
    this.setState({
      loading: false,
      factura: null,
      proveedorNombre: "",
      monedaAbreviacion: "",
      fechaFacturaOriginal: null,
    });

    if (exitoso) {
      this.props.onSuccess();
    } else {
      this.props.onCancel();
    }
  }

  onFinish(values) {
    this.setState({ loading: true });

    const esCajaChica = Boolean(this.state.factura?.cajaId);

    // Los tres selects con allowClear devuelven undefined al limpiarse, y JSON.stringify borra
    // las claves undefined: sin este null el backend nunca se enteraria de que se vaciaron.
    const payload = {
      empresaId: this.props.empresaId,
      facturaId: this.props.facturaId,
      codigoFactura: values.codigoFactura,
      uuid: values.uuid,
      descripcion: values.descripcion,
      usoDeCfdiId: values.usoDeCfdiId ?? null,
      formaDePagoId: values.formaDePagoId ?? null,
      metodoPago: values.metodoPago ?? null,
      fechaFactura: values.fechaFactura,
    };

    // Caja chica no captura credito: nace con 0 dias y pagada el mismo dia, y esos campos ni
    // siquiera se pintan en el formulario.
    if (!esCajaChica) {
      payload.diasCredito = values.diasCredito;
      payload.fechaPago = values.fechaPago;
      payload.fechaVencimiento = values.fechaVencimiento;
    }

    axios
      .post("facturas/editarDatosFactura", payload, {
        headers: this.props.headersToken,
      })
      .then((res) => {
        this.setState({ loading: false });
        if (res.data.success === 2) {
          message.success("Datos actualizados correctamente");
          // El backend puede haber guardado todo menos la fecha de factura (activos fijos,
          // movimientos conciliados o poliza no localizable); ese aviso no se puede perder.
          if (res.data.aviso) message.warn(res.data.aviso, 8);
          this.cerrarModal(true);
        } else if (res.data.success === 1) {
          message.warn(res.data.message);
        } else {
          message.error(res.data.message || "Error al actualizar los datos");
        }
      })
      .catch((err) => {
        this.setState({ loading: false });
        console.error(err);
        message.error("Error de conexion");
      });
  }

  onFinishFailed(e) {
    ErrorItem(e);
  }

  render() {
    const { factura, fechaFacturaOriginal } = this.state;
    const esCajaChica = Boolean(factura?.cajaId);
    const cancelada = factura?.estado === 0;

    return (
      <ModalItem
        title={
          "Editar Datos Factura" +
          (factura?.numeroFactura ? " - " + factura.numeroFactura : "")
        }
        visible={this.props.visible}
        spinning={this.state.loading}
        onCancel={() => this.cerrarModal()}
        width={window.innerWidth > 1000 ? "55%" : "95%"}
        paddingTop
      >
        {cancelada ? (
          <Alert
            type="error"
            showIcon
            message="Esta factura esta cancelada"
            description="No se pueden editar los datos de una factura cancelada."
          />
        ) : (
          <Form
            ref={this.formRef}
            name="form-editar-datos-factura"
            layoutForm="vertical"
            onFinish={this.onFinish.bind(this)}
            onFinishFailed={this.onFinishFailed}
            bottom={
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: 20,
                }}
              >
                <ButtonItem
                  type="primary"
                  onClick={() => this.cerrarModal()}
                  rojo
                  style={{ marginRight: 8 }}
                >
                  Cancelar
                </ButtonItem>
                <ButtonItem type="primary" htmlType="submit">
                  Guardar
                </ButtonItem>
              </div>
            }
          >
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 15, fontSize: "12px" }}
              message="Solo se editan datos de cabecera. El proveedor, el proyecto, los insumos, los impuestos y los importes no se pueden cambiar desde aqui."
            />

            {factura ? (
              <Row
                gutter={[16, 4]}
                style={{
                  marginBottom: 15,
                  padding: "10px",
                  background: "#fafafa",
                  borderRadius: "6px",
                  border: "1px solid #f0f0f0",
                  fontSize: "12px",
                }}
              >
                <Col xs={12} lg={6}>
                  <b>Numero: </b>
                  {factura.numeroFactura || "-"}
                </Col>
                <Col xs={12} lg={10}>
                  <b>Proveedor: </b>
                  {this.state.proveedorNombre || "-"}
                </Col>
                <Col xs={12} lg={8}>
                  <b>Total a pagar: </b>
                  {dineroDisabledMask(factura.totalPagar)}{" "}
                  {this.state.monedaAbreviacion}
                </Col>
              </Row>
            ) : null}

            <Row gutter={[16, 0]}>
              <Col xs={24} sm={12} lg={8}>
                <FormItem name="codigoFactura" label="Codigo Factura" margin pattern>
                  <InputItem placeholder="Codigo Factura" />
                </FormItem>
              </Col>
              <Col xs={24} sm={12} lg={8}>
                <FormItem name="uuid" label="UUID" margin pattern>
                  <InputItem placeholder="UUID" />
                </FormItem>
              </Col>
              <Col xs={24} sm={12} lg={8}>
                <FormItem name="usoDeCfdiId" label="Uso de CFDI" margin>
                  <SelectItem placeholder="Uso de CFDI" allowClear>
                    {this.state.objetoUsosDeCFDI}
                  </SelectItem>
                </FormItem>
              </Col>
              <Col xs={24} sm={12} lg={8}>
                <FormItem name="formaDePagoId" label="Forma de Pago" margin>
                  <SelectItem placeholder="Forma de Pago" allowClear>
                    {this.state.objetoFormasDePago}
                  </SelectItem>
                </FormItem>
              </Col>
              <Col xs={24} sm={12} lg={8}>
                <FormItem name="metodoPago" label="Metodo de Pago" margin>
                  <SelectItem placeholder="Metodo de Pago" allowClear>
                    <Option value={1}>PUE - Pago en Una sola Exhibición</Option>
                    <Option value={2}>PPD - Pago en Parcialidades o Diferido</Option>
                  </SelectItem>
                </FormItem>
              </Col>
              <Col xs={24} sm={12} lg={8}>
                <FormItem label="Fecha de Factura" name="fechaFactura" required margin>
                  <DatePickerItem
                    placeholder="Fecha de Factura"
                    allowClear={false}
                  />
                </FormItem>
              </Col>

              {esCajaChica ? null : (
                <>
                  <Col xs={24} sm={12} lg={8}>
                    {/* numberCero y no number: una factura de contado va con 0 dias, y la
                        regla number exige mayor a cero. */}
                    <FormItem
                      label="Dias de credito"
                      name="diasCredito"
                      required
                      numberCero
                      margin
                    >
                      <InputNumberItem placeholder="Dias de credito" />
                    </FormItem>
                  </Col>
                  <Col xs={24} sm={12} lg={8}>
                    <FormItem
                      label="Fecha probable Pago"
                      name="fechaPago"
                      required
                      margin
                    >
                      <DatePickerItem
                        placeholder="Fecha probable Pago"
                        allowClear={false}
                      />
                    </FormItem>
                  </Col>
                  <Col xs={24} sm={12} lg={8}>
                    <FormItem
                      label="Fecha Vencimiento"
                      name="fechaVencimiento"
                      required
                      margin
                    >
                      <DatePickerItem
                        placeholder="Fecha Vencimiento"
                        allowClear={false}
                      />
                    </FormItem>
                  </Col>
                </>
              )}

              <Col xs={24}>
                <FormItem name="descripcion" label="Descripcion" margin pattern>
                  <InputItem placeholder="Descripcion" />
                </FormItem>
              </Col>
            </Row>

            <FormItem
              noStyle
              shouldUpdate={(prev, curr) => prev.fechaFactura !== curr.fechaFactura}
            >
              {({ getFieldValue }) => {
                const fechaActual = getFieldValue("fechaFactura");
                if (
                  !fechaActual ||
                  !fechaFacturaOriginal ||
                  moment(fechaActual).isSame(fechaFacturaOriginal, "day")
                ) {
                  return null;
                }
                return (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginTop: 5, fontSize: "12px" }}
                    message="Vas a mover la fecha de la factura"
                    description="Se moveran tambien la poliza de recepcion de esta factura y sus cargos y abonos. No se mueven las polizas de los pagos, que conservan su propia fecha."
                  />
                );
              }}
            </FormItem>
          </Form>
        )}
      </ModalItem>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    headersToken: state.user.headersToken,
    empresaId: state.empresa.id,
  };
};

export default connect(mapStateToProps)(ModalEditarDatosFactura);
